/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import '../../../types/docs-registry.d.ts';
import { IDocNavItem } from "../DocumentationTypes";
import { IDocEntry } from "docs-registry";

/**
 * Formats a path segment into a readable title
 */
export function formatTitle(text: string): string {
	return text
		.replace(/[-_]/g, ' ')
		.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Builds a navigation tree from flat docs registry
 */
export function buildNavTree(docsRegistry: IDocEntry[]): IDocNavItem[] {
	const tree: Map<string, IDocNavItem> = new Map();

	// Build folder structure
	for (const doc of docsRegistry) {
		const parts = doc.path.split('/');
		let currentPath = '';

		// Create folder nodes
		for (let i = 0; i < parts.length - 1; i++) {
			const part = parts[i];
			const parentPath = currentPath;
			currentPath = currentPath ? `${currentPath}/${part}` : part;

			if (!tree.has(currentPath)) {
				tree.set(currentPath, {
					path: currentPath,
					title: formatTitle(part),
					filePath: currentPath,
					isFolder: true,
					children: []
				});

				// Add to parent
				if (parentPath && tree.has(parentPath)) {
					const parentNode = tree.get(parentPath);
					const currentNode = tree.get(currentPath);
					if (parentNode && currentNode && parentNode.children) {
						parentNode.children.push(currentNode);
					}
				}
			}
		}

		// Create doc node
		const docNode: IDocNavItem = {
			path: doc.path,
			title: doc.metadata.title,
			filePath: doc.filePath
		};

		// Add to parent folder or root
		const parentPath = parts.slice(0, -1).join('/');
		if (parentPath && tree.has(parentPath)) {
			const parentNode = tree.get(parentPath);
			if (parentNode && parentNode.children) {
				parentNode.children.push(docNode);
			}
		} else {
			// Root level doc
			tree.set(doc.path, docNode);
		}
	}

	// Get root level items
	const rootItems: IDocNavItem[] = [];
	for (const [path, item] of tree) {
		if (!path.includes('/')) {
			rootItems.push(item);
		}
	}

	// Sort items
	return sortNavItems(rootItems, docsRegistry);
}

/**
 * Sorts navigation items by folders first, then by order property, then alphabetically
 */
export function sortNavItems(items: IDocNavItem[], docsRegistry: IDocEntry[]): IDocNavItem[] {
	return items.sort((a, b) => {
		// Folders first
		if (a.isFolder && !b.isFolder) return -1;
		if (!a.isFolder && b.isFolder) return 1;
		
		// For documents, check if they have order property
		if (!a.isFolder && !b.isFolder) {
			const docA = docsRegistry.find(d => d.path === a.path);
			const docB = docsRegistry.find(d => d.path === b.path);
			const orderA = docA?.metadata.order ?? Infinity;
			const orderB = docB?.metadata.order ?? Infinity;
			
			if (orderA !== orderB) {
				return orderA - orderB;
			}
		}
		
		// Then by title
		return a.title.localeCompare(b.title);
	}).map(item => {
		if (item.children) {
			item.children = sortNavItems(item.children, docsRegistry);
		}
		return item;
	});
}

/**
 * Filters navigation items by search query
 */
export function filterNav(items: IDocNavItem[], query: string): IDocNavItem[] {
	const lowerQuery = query.toLowerCase();
	const filtered: IDocNavItem[] = [];

	for (const item of items) {
		if (item.title.toLowerCase().includes(lowerQuery)) {
			filtered.push(item);
		} else if (item.children) {
			const filteredChildren = filterNav(item.children, query);
			if (filteredChildren.length > 0) {
				filtered.push({
					...item,
					children: filteredChildren
				});
			}
		}
	}

	return filtered;
}
